import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import {
  Clock,
  TrendingUp,
  TrendingDown,
  Calendar,
  Activity,
  BarChart3,
  Zap,
  Sun,
  Moon,
  Sunrise,
  Loader2
} from 'lucide-react';

// Hour chart component
const HourlyChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  
  const maxCount = Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between h-32 gap-0.5">
        {data.map((item, index) => (
          <div key={index} className="flex flex-col items-center flex-1">
            <motion.div
              className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-sm min-h-[2px]"
              initial={{ height: 0 }}
              animate={{ height: `${(item.count / maxCount) * 100}%` }}
              transition={{ duration: 0.5, delay: index * 0.02 }}
              title={`${item.count} actividades a las ${item.hour}:00`}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-xs text-slate-500">
        <span>0h</span>
        <span>6h</span>
        <span>12h</span>
        <span>18h</span>
        <span>23h</span>
      </div>
    </div>
  );
};

// Day of week chart component
const DailyChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const colors = [
    'from-blue-500 to-blue-400',
    'from-cyan-500 to-cyan-400',
    'from-teal-500 to-teal-400',
    'from-green-500 to-green-400',
    'from-emerald-500 to-emerald-400',
    'from-amber-500 to-amber-400',
    'from-orange-500 to-orange-400'
  ];
  
  return (
    <div className="flex items-end justify-between h-32 gap-2">
      {data.map((item, index) => (
        <div key={index} className="flex flex-col items-center flex-1">
          <span className="text-xs text-slate-400 mb-1">{item.count}</span>
          <motion.div
            className={`w-full bg-gradient-to-t ${colors[index]} rounded-t-md min-h-[4px]`}
            initial={{ height: 0 }}
            animate={{ height: `${(item.count / maxCount) * 100}%` }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          />
          <span className="text-xs text-slate-400 mt-2">{item.name.slice(0, 3)}</span>
        </div>
      ))}
    </div>
  );
};

// Activity type breakdown
const ActivityBreakdown = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-4 text-slate-400 text-sm">
        Sin actividad registrada
      </div>
    );
  }
  
  const total = data.reduce((sum, item) => sum + item.count, 0);
  const colors = [
    'bg-blue-500',
    'bg-cyan-500',
    'bg-teal-500',
    'bg-green-500',
    'bg-amber-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-red-500'
  ];
  
  return (
    <div className="space-y-2">
      {data.slice(0, 5).map((item, index) => (
        <div key={item.type} className="flex items-center gap-2">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-300 truncate">{item.label}</span>
              <span className="text-xs text-slate-400">{item.count}</span>
            </div>
            <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${colors[index % colors.length]}`}
                initial={{ width: 0 }}
                animate={{ width: `${(item.count / total) * 100}%` }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Insights card
const InsightsCard = ({ peakAnalysis, weeklyComparison }) => {
  const getPeriodIcon = (hour) => {
    if (hour >= 6 && hour < 12) return <Sunrise className="w-4 h-4 text-amber-400" />;
    if (hour >= 12 && hour < 18) return <Sun className="w-4 h-4 text-yellow-400" />;
    if (hour >= 18 && hour < 22) return <Moon className="w-4 h-4 text-blue-400" />;
    return <Moon className="w-4 h-4 text-slate-400" />;
  };
  
  return (
    <div className="space-y-3">
      {/* Peak hour */}
      {peakAnalysis?.peak_hour !== null && (
        <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
          {getPeriodIcon(peakAnalysis.peak_hour)}
          <div className="flex-1">
            <p className="text-sm text-white">Hora pico: {peakAnalysis.peak_hour}:00</p>
            <p className="text-xs text-slate-400">{peakAnalysis.peak_count} actividades</p>
          </div>
        </div>
      )}
      
      {/* Weekly comparison */}
      {weeklyComparison && (
        <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
          {weeklyComparison.trend === 'up' ? (
            <TrendingUp className="w-4 h-4 text-green-400" />
          ) : weeklyComparison.trend === 'down' ? (
            <TrendingDown className="w-4 h-4 text-red-400" />
          ) : (
            <Activity className="w-4 h-4 text-slate-400" />
          )}
          <div className="flex-1">
            <p className="text-sm text-white">
              {weeklyComparison.trend === 'up' ? '+' : ''}
              {weeklyComparison.change_percent}% vs semana anterior
            </p>
            <p className="text-xs text-slate-400">
              Esta semana: {weeklyComparison.this_week} | Anterior: {weeklyComparison.last_week}
            </p>
          </div>
        </div>
      )}
      
      {/* Insights */}
      {peakAnalysis?.insights?.map((insight, index) => (
        <div key={index} className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-lg">
          <Zap className="w-4 h-4 text-blue-400" />
          <p className="text-sm text-slate-300">{insight.message}</p>
        </div>
      ))}
    </div>
  );
};

const ActivityMetrics = ({ showTitle = true }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(30);
  
  useEffect(() => {
    fetchActivityData();
  }, [period]);
  
  const fetchActivityData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/analytics/activity/summary?days=${period}`);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching activity data:', err);
      setError('Error al cargar métricas de actividad');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          <span className="ml-2 text-slate-400">Cargando métricas...</span>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <div className="text-center py-4 text-red-400">{error}</div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="activity-metrics">
      {/* Header */}
      {showTitle && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Activity className="w-5 h-5 text-purple-400" />
            <h2 className="text-lg font-semibold font-['Outfit']">Métricas de Actividad</h2>
          </div>
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300"
            data-testid="activity-period-select"
          >
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
          </select>
        </div>
      )}
      
      {/* Stats summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-400">Total Actividades</span>
            <Activity className="w-4 h-4 text-blue-400" />
          </div>
          <p className="text-2xl font-bold text-white">
            {data?.peak_analysis?.total_activities || 0}
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-400">Hora Pico</span>
            <Clock className="w-4 h-4 text-amber-400" />
          </div>
          <p className="text-2xl font-bold text-white">
            {data?.peak_analysis?.peak_hour !== null 
              ? `${data.peak_analysis.peak_hour}:00` 
              : '--'}
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-400">Esta Semana</span>
            <Calendar className="w-4 h-4 text-green-400" />
          </div>
          <p className="text-2xl font-bold text-white">
            {data?.weekly_comparison?.this_week || 0}
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-400">Tendencia</span>
            {data?.weekly_comparison?.trend === 'up' ? (
              <TrendingUp className="w-4 h-4 text-green-400" />
            ) : data?.weekly_comparison?.trend === 'down' ? (
              <TrendingDown className="w-4 h-4 text-red-400" />
            ) : (
              <Activity className="w-4 h-4 text-slate-400" />
            )}
          </div>
          <p className={`text-2xl font-bold ${
            data?.weekly_comparison?.trend === 'up' 
              ? 'text-green-400' 
              : data?.weekly_comparison?.trend === 'down'
                ? 'text-red-400'
                : 'text-white'
          }`}>
            {data?.weekly_comparison?.change_percent > 0 ? '+' : ''}
            {data?.weekly_comparison?.change_percent || 0}%
          </p>
        </motion.div>
      </div>
      
      {/* Charts row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Hourly activity */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-blue-400" />
              <h3 className="text-sm font-medium text-white">Actividad por Hora</h3>
            </div>
          </div>
          <HourlyChart data={data?.hourly_activity} />
        </motion.div>
        
        {/* Daily activity */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-green-400" />
              <h3 className="text-sm font-medium text-white">Actividad por Día</h3>
            </div>
          </div>
          <DailyChart data={data?.daily_activity} />
        </motion.div>
      </div>
      
      {/* Second row: Activity breakdown and insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Activity breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-4 h-4 text-purple-400" />
              <h3 className="text-sm font-medium text-white">Tipos de Actividad</h3>
            </div>
          </div>
          <ActivityBreakdown data={data?.activity_breakdown} />
        </motion.div>
        
        {/* Insights */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Zap className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-medium text-white">Insights</h3>
            </div>
          </div>
          <InsightsCard 
            peakAnalysis={data?.peak_analysis}
            weeklyComparison={data?.weekly_comparison}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default ActivityMetrics;
